'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { EndpointSourceType } from '@terra/shared-schemas-orchestrator';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  EndpointMethodBadge,
  ImageWithSkeleton,
  LabelDefault,
  MetaDefault,
  Tooltip,
  getRoleDisplayName,
} from '@terra/ui-design-system';
import { Image01Icon } from '@terra/ui-icons';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { useWebpageDetails } from '../../../hooks/api/useWebpageDetails';
import {
  useAssetsTableContext,
  type AssetsTableScreenshotPage,
  type AssetsTableScreenshotRequest,
} from './AssetsTableContext';
import { rowClickBlockerProps } from '../rowClickBlocker';
import { ENDPOINT_SOURCE_TRANSLATION_KEY_BY_VALUE } from '../endpoint-source-labels';

const THUMBNAIL_WIDTH = '48px';
const THUMBNAIL_HEIGHT = '28px';

function EmptyCellValue() {
  return <MetaDefault color="fg.muted">-</MetaDefault>;
}

export function AssetTextCell({ value }: { value: string }) {
  if (!value) {
    return <EmptyCellValue />;
  }

  return (
    <BodyDefault truncate title={value}>
      {value}
    </BodyDefault>
  );
}

export function AssetMethodCell({ method }: { method: string }) {
  return <EndpointMethodBadge method={method} />;
}

function AssetOverflowValues({
  first,
  others,
}: {
  first: ReactNode;
  others: readonly string[];
}) {
  return (
    <HStack gap={1} minW={0}>
      {first}
      {others.length > 0 ? (
        <Tooltip title={others.join(', ')}>
          <Box flexShrink={0}>
            <Badge size="sm" variant="outline" borderRadius="full">
              {`+${others.length}`}
            </Badge>
          </Box>
        </Tooltip>
      ) : null}
    </HStack>
  );
}

export function AssetRolesCell({ roles }: { roles: readonly string[] }) {
  const [firstRole, ...otherRoles] = [...new Set(roles)].map(
    getRoleDisplayName,
  );

  if (!firstRole) {
    return <EmptyCellValue />;
  }

  return (
    <AssetOverflowValues
      first={
        <LabelDefault truncate px={1.5}>
          {firstRole}
        </LabelDefault>
      }
      others={otherRoles}
    />
  );
}

export function AssetSourcesCell({
  sources,
}: {
  sources: readonly EndpointSourceType[];
}) {
  const t = useTranslations('proxy.endpointDetails.sourceValues');
  const [firstSource, ...otherSources] = [...new Set(sources)].map((source) =>
    t(ENDPOINT_SOURCE_TRANSLATION_KEY_BY_VALUE[source]),
  );

  if (!firstSource) {
    return <EmptyCellValue />;
  }

  return (
    <AssetOverflowValues
      first={<BodyDefault truncate>{firstSource}</BodyDefault>}
      others={otherSources}
    />
  );
}

export function AssetCreatedAtCell({ createdAt }: { createdAt: Date }) {
  const dateFormatter = useDateFormatter();

  return (
    <MetaDefault color="fg.muted">
      {dateFormatter.format(new Date(createdAt))}
    </MetaDefault>
  );
}

export function AssetApiTypeCell({ label }: { label: string | null }) {
  if (!label) {
    return <EmptyCellValue />;
  }

  return (
    <Badge size="sm" variant="outline" borderRadius="full">
      {label}
    </Badge>
  );
}

function AssetScreenshotThumbnail({
  page,
}: {
  page: AssetsTableScreenshotPage;
}) {
  const t = useTranslations('proxy.assets.table');
  const { data: webpageDetails } = useWebpageDetails({
    webpageId: page.id,
    enabled: page.signedUrl === null,
  });

  return (
    <ImageWithSkeleton
      src={page.signedUrl ?? webpageDetails?.signedUrl ?? null}
      alt={page.url}
      fallback={
        <Box color="fg.subtle" display="inline-flex">
          <Image01Icon size={14} aria-label={t('noScreenshot')} />
        </Box>
      }
    />
  );
}

export function AssetScreenshotCell({
  request,
  additionalCount = 0,
}: {
  request: AssetsTableScreenshotRequest | null;
  additionalCount?: number;
}) {
  const t = useTranslations('proxy.assets.table');
  const { onOpenScreenshots } = useAssetsTableContext();

  if (!request) {
    return <EmptyCellValue />;
  }

  return (
    <HStack gap={1.5} h="full" alignItems="center">
      <Box
        role="button"
        tabIndex={0}
        aria-label={t('openScreenshot')}
        w={THUMBNAIL_WIDTH}
        h={THUMBNAIL_HEIGHT}
        flexShrink={0}
        borderRadius="sm"
        overflow="hidden"
        borderWidth={1}
        borderColor="border.muted"
        bg="bg.subtle"
        cursor="zoom-in"
        _hover={{ borderColor: 'border.emphasized' }}
        {...rowClickBlockerProps}
        onClick={(event) => {
          event.stopPropagation();
          onOpenScreenshots(request);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            onOpenScreenshots(request);
          }
        }}
      >
        <AssetScreenshotThumbnail page={request.page} />
      </Box>
      {additionalCount > 0 ? (
        <Box flexShrink={0}>
          <Badge size="sm" variant="outline" borderRadius="full">
            {`+${additionalCount}`}
          </Badge>
        </Box>
      ) : null}
    </HStack>
  );
}
